import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Alert, Select, Typography } from "antd";
import editPencil from "assets/images/new-questionnaire/edit_pencil.svg";
import { CategoryPrompt } from "components/pages/Questionnaire/constants/categories";
import DOMPurify from "dompurify"; // To sanitize HTML input
import { IncomeExpenseCategory } from "enums/banking";
import { ProfileType } from "enums/questionnaire";
import { useAppSelector } from "redux/hooks";
import { selectDocument } from "redux/reducers/document";
import { ICreateClientBankTransaction } from "types/banking";
import { logError } from "utils/sentry";

import InlineEditableTextInput from "../../Shared/InlineEditableTextInput";

interface AddTransactionProps {
    taxProfileType: ProfileType;
    taxProfileId: string;
    handleClose: () => void;
    categories: string[];
    createTransaction: (_data: ICreateClientBankTransaction) => Promise<void>;
}

const AddTransaction: React.FC<AddTransactionProps> = ({
    taxProfileType,
    taxProfileId,
    handleClose,
    categories,
    createTransaction,
}) => {
    const inputTotalRef = useRef<HTMLInputElement>(null);
    const [isEditingTotalAmount, setIsEditingTotalAmount] = useState(false);
    const [currentAmount, setCurrentAmount] = useState<string>("0.00");
    const [originalAmount, setOriginalAmount] = useState<string>("0.00");

    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [description, setDescription] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<IncomeExpenseCategory[]>([]);
    const { currentDocumentYear } = useAppSelector(selectDocument);

    const error = useMemo(() => {
        if (description.trim() === "") {
            return "Description is required";
        }
        if (currentAmount === undefined || currentAmount === "" || Number(currentAmount) === 0) {
            return "Amount is required";
        }
        if (selectedCategory[0] === undefined || selectedCategory[0] === "No Category") {
            return "Category is required";
        }
        return undefined;
    }, [selectedCategory, currentAmount, description]);

    const categoryInfo = useMemo(() => {
        const rawMarkdown = CategoryPrompt[taxProfileType]?.[selectedCategory[0]] ?? "";
        const sanitizedMarkdown = DOMPurify.sanitize(rawMarkdown, {
            ADD_ATTR: ["target"],
        });
        return sanitizedMarkdown;
    }, [selectedCategory, taxProfileType]);

    const handleTotalWrapperClick = useCallback(() => {
        if (inputTotalRef.current) {
            inputTotalRef.current.focus();
        }
    }, []);

    const handleTotalChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(",", ".");
        if (/^-?\d*\.?\d{0,2}$/.test(value)) {
            setCurrentAmount(value);
        }
    }, []);

    const handleChangeCategory = useCallback((value: IncomeExpenseCategory[]) => {
        setSelectedCategory(value);
    }, []);

    const handleDescriptionChange = useCallback((e: ChangeEvent<HTMLInputElement>, _category: string) => {
        setDescription(e.target.value);
    }, []);

    const handleCreateTransaction = useCallback(async () => {
        if (selectedCategory[0] === "No Category") {
            return;
        }

        const endYear = currentDocumentYear.split("/")[1];
        const endDateObj = new Date(`${endYear}-04-05T23:59:59Z`);

        const today = new Date();

        // Determine whether to use today or endDate
        const bookingDateTime = today <= endDateObj ? today.toISOString() : endDateObj.toISOString();

        const createBody: ICreateClientBankTransaction = {
            category: selectedCategory[0],
            amount: Number(`-${Math.abs(Number(Number(currentAmount)))}`),
            description,
            approved: true,
            rejected: false,
            taxProfileType,
            taxProfileId,
            taxYear: currentDocumentYear,
            currencyCode: "GBP",
            bookingDateTime: bookingDateTime,
        };
        try {
            await createTransaction(createBody);
            handleClose();
            setIsEditingTotalAmount(false);
            setOriginalAmount("0.00");
            setCurrentAmount("0.00");
            setIsEditingDescription(false);
            setDescription("");
            setSelectedCategory([IncomeExpenseCategory.NoCategory]);
        } catch (error) {
            logError(error, {
                function: "handleCreateTransaction",
                createBody,
            });
        }
    }, [createTransaction, taxProfileType, taxProfileId, selectedCategory, description, handleClose, currentDocumentYear, currentAmount]);

    const handleKeyDown = useCallback((e: any) => {
        if (e.key === "Enter") {
            setIsEditingTotalAmount(false);
            e.currentTarget.blur();
        }
        // if the the user clicks to edit, clear
    }, [setIsEditingTotalAmount]);

    useEffect(() => {
        setSelectedCategory([IncomeExpenseCategory.NoCategory]);
        setCurrentAmount("0.00");
        setOriginalAmount("0.00");
        setDescription("");
    }, []);

    return (
        <div className="updateTransaction">
            <div className="updateTransaction__content">
                <div className="expenses__swipeable-card-content">
                    <div className="expenses__swipeable-card-amount mt10 mb10">
                        £{currentAmount?.toString() || "0.00"}
                    </div>
                    <div className="expenses__swipeable-card__info">
                        <Typography className="expenses__swipeable-card-description">
                            {description || "No description provided"}
                        </Typography>
                        <InlineEditableTextInput
                            categoryName="Description"
                            text={description}
                            onTextChange={handleDescriptionChange}
                            onEditToggle={() => setIsEditingDescription(!isEditingDescription)}
                            editIcon={editPencil}
                            style={{ height: "auto", minHeight: "52px" }}
                        />

                        <div className="new-questionnaire__amount-card-wrapper mt12" onClick={handleTotalWrapperClick}>
                            <div className="new-questionnaire__income-card-total-text">Amount</div>
                            {isEditingTotalAmount ? (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                                    <input
                                        data-testid="total-amount-input"
                                        className="new-questionnaire__edit-expense-input mr15"
                                        onChange={handleTotalChange}
                                        type="text"
                                        inputMode="decimal"
                                        pattern="[0-9]*"
                                        autoFocus={true}
                                        value={currentAmount?.toString()}
                                        ref={inputTotalRef}
                                        onBlur={() => {
                                            // Revert to the original value if the input is empty
                                            if (!currentAmount.trim()) {
                                                setCurrentAmount(originalAmount);
                                            }
                                            setIsEditingTotalAmount(false);
                                        }}
                                        onKeyDown={(e: any) => handleKeyDown(e)}
                                    />
                                </div>
                            ) : (
                                <div className="new-questionnaire__income-card-total-amount-wrapper" style={{ backgroundColor: "transparent" }} onClick={() => {
                                    setIsEditingTotalAmount(true);
                                    setOriginalAmount(currentAmount);
                                    setCurrentAmount("");
                                }}>
                                    <img src={editPencil} alt="edit" style={{ display: "block" }} />
                                    <div className="new-questionnaire__income-card-total-amount">£{currentAmount?.toString()}</div>
                                </div>
                            )}
                        </div>
                        <div className="new-questionnaire__amount-card-wrapper mt12">
                            <div className="new-questionnaire__income-card-total-text">Category</div>
                            <div className="new-questionnaire__income-card-total-amount-wrapper" style={{ backgroundColor: "transparent", cursor: "default" }}>
                                <Select
                                    variant="borderless"
                                    className="swipeable-card-tag"
                                    style={{ fontSize: "12px", width: "fit-content" }}
                                    dropdownStyle={{ width: 230 }}
                                    value={selectedCategory[0] === "No Category" ? "Select Category" : selectedCategory}
                                    onSelect={value => handleChangeCategory([value as IncomeExpenseCategory])}
                                    placement="bottomLeft"
                                    placeholder="Select Category"

                                >
                                    {categories.map(category => (
                                        <Select.Option style={{ padding: "10px" }} key={category} size="large" value={category}>{category}</Select.Option>
                                    ))}

                                </Select>
                            </div>

                        </div>
                        <div
                            className="updateTransaction__category-info"
                            dangerouslySetInnerHTML={{ __html: categoryInfo }}
                        />
                        {error && (
                            <Alert
                                className="w100"
                                message={error}
                                type="error"
                                showIcon
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className="updateTransaction__action-buttons">
                <button
                    className={`shared__primary-button w100 ${error ? "disabled" : ""}`}
                    onClick={handleCreateTransaction}
                    disabled={error !== undefined}
                >
                    Update
                </button>
            </div>
        </div>
    );
};

export default AddTransaction;
